import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import TravellerSearchSelect from '@/components/traveller/TravellerSearchSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only. Moves a quote/trip to a different traveller. The backend cascades
 * the change to the trip's invoices + ledger entries, logs an audit entry, and
 * recomputes LTV for both travellers.
 */
const ReassignTripDialog = ({ open, onOpenChange, trip, onDone }) => {
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const tripRef = trip?.displayTripId || trip?.tripId || '';
  const tripKey = trip?.tripId || trip?.id || trip?._id;

  const close = () => {
    setSelected(null);
    onOpenChange(false);
  };

  const reassign = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/trips/${encodeURIComponent(tripKey)}/reassign`,
        { newUcc: selected.ucc },
        { withCredentials: true },
      );
      toast.success(`${tripRef} reassigned to ${selected.fullName || selected.ucc}`);
      onDone?.(res.data, selected);
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reassignment failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle>Reassign {tripRef}</DialogTitle>
          <DialogDescription>
            Move this trip to a different traveller. Its invoices and ledger entries
            will follow the new owner, and lifetime value is recomputed for both.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2">
            <span className="font-semibold text-slate-800">{trip?.guestName || '—'}</span>
            <ArrowRight className="h-4 w-4 text-slate-400" />
            <span className={selected ? 'font-semibold text-teal-700' : 'text-slate-400 italic'}>
              {selected ? (selected.fullName || selected.ucc) : 'choose traveller'}
            </span>
          </div>

          {!selected ? (
            <TravellerSearchSelect
              allowCreate={false}
              autoFocus
              placeholder="Search the traveller to reassign to…"
              onSelect={(c) => c && setSelected(c)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setSelected(null)}
              data-testid="reassign-change-traveller"
              className="text-xs text-teal-700 font-semibold hover:underline"
            >
              Choose a different traveller
            </button>
          )}

          {selected && (
            <div className="flex items-start gap-2 text-[12px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                This rewrites the financial linkage for <strong>{tripRef}</strong>. Issued
                invoice documents keep their printed details for audit integrity.
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="reassign-cancel-btn">Cancel</Button>
          <Button
            onClick={reassign}
            disabled={!selected || submitting}
            data-testid="reassign-confirm-btn"
            className="bg-teal-700 hover:bg-teal-800 text-white"
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Confirm reassignment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReassignTripDialog;
